/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useQuickList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing quick list modal for domain selection.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../store/auth';
import { getDomains } from '../api/telephonyDomainsApi';
import type { Domain } from '../types';

export function useQuickList(mode: 'create' | 'edit', id: string | undefined, duplicateId: string | null) {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const tenantSlug = user?.tenant?.slug ?? 'main';

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListDomains, setQuickListDomains] = useState<Domain[]>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  // Load quick list domains
  const loadQuickListDomains = useCallback(async () => {
    if (quickListLoadingRef.current) return;
    
    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const response = await getDomains({
        page: 1,
        limit: 100,
        sortBy: 'domainName',
        sortOrder: 'asc',
        tenantSlug,
        userId: user?.id
      });

      let allDomains = Array.isArray(response.data) ? response.data : [];
      
      // If we got 100 results, try to load more pages
      if (allDomains.length === 100 && response.pagination) {
        const totalPages = response.pagination.totalPages || Math.ceil((response.pagination.total || 0) / 100);
        for (let page = 2; page <= totalPages && page <= 10; page++) { // Limit to 10 pages max (1000 domains)
          const nextResponse = await getDomains({
            page,
            limit: 100,
            sortBy: 'domainName',
            sortOrder: 'asc',
            tenantSlug,
            userId: user?.id
          });
          
          if (Array.isArray(nextResponse.data)) {
            allDomains = [...allDomains, ...nextResponse.data];
          }
        }
      }
      
      setQuickListDomains(allDomains);
    } catch (error) {
      setQuickListDomains([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, [tenantSlug, user?.id]);

  // Load quick list when modal opens
  useEffect(() => {
    if (showQuickList && quickListDomains.length === 0 && !quickListLoadingRef.current) {
      loadQuickListDomains();
    }
  }, [showQuickList, quickListDomains.length, loadQuickListDomains]);

  // Filter domains client-side
  const filteredQuickList = useMemo(() => {
    const domains = quickListDomains.filter(d => d.id != null);
    if (!quickListSearch.trim()) {
      return domains;
    }
    const searchLower = quickListSearch.toLowerCase();
    return domains.filter(
      (domain) =>
        domain.domainName?.toLowerCase().includes(searchLower) ||
        domain.domainDescription?.toLowerCase().includes(searchLower) ||
        (domain as any).tenantName?.toLowerCase().includes(searchLower)
    );
  }, [quickListDomains, quickListSearch]);

  // Handle item select
  const handleItemSelect = useCallback((item: Domain) => {
    setShowQuickList(false);
    if (item.id) {
      navigate(`/admin/multi-tenant/domains/${item.id}`);
    }
  }, [navigate]);

  return {
    showQuickList,
    setShowQuickList,
    quickListSearch,
    setQuickListSearch,
    filteredQuickList,
    isQuickListLoading,
    handleItemSelect,
    currentItemId: mode === 'edit' && !duplicateId ? id : null
  };
}


